import React, { useMemo, useState, useRef, useCallback } from 'react';
import {
  FileText,
  Loader2,
  AlertCircle,
  Search,
  X,
  ArrowLeft,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Link2,
  BookOpen,
  ExternalLink,
  Crosshair,
  GitCompare
} from 'lucide-react';
import { 
  useListQAResultsNewQuery,
  useGetQAResultPagesQuery,
  useGetQAResultPageDetailQuery,
  type QAResultNew,
  type PageSummaryItem,
  type PageDetailResponse,
} from '../../redux/api/qaApi';
import { useAppSelector } from '../../redux/hooks';

interface PageListTabProps {
  qaResultId?: string;
  documentId?: string;
}

// Using PageSummaryItem from API instead of local PageInfo

const PageListTab: React.FC<PageListTabProps> = ({ qaResultId }) => {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const tenantId = currentSite?.tenant_id;
  const siteId = currentSite?.site_id;

  // Fetch QA results for the selected site
  const { data: qaResultsData, isLoading: isLoadingResults } = useListQAResultsNewQuery(
    { 
      site_id: siteId || undefined,
      limit: 100, 
      offset: 0 
    },
    { skip: !tenantId || !siteId }
  );

  // Determine which QA result to show page list for
  const selectedQAResultId = useMemo(() => {
    if (qaResultId) return qaResultId;
    // If no specific result selected, use the most recent one for this site
    if (qaResultsData?.items && qaResultsData.items.length > 0) {
      return qaResultsData.items[0].id;
    }
    return null;
  }, [qaResultId, qaResultsData]);

  const [selectedPageNumber, setSelectedPageNumber] = useState<number | null>(null);
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set());
  const [typeSearch, setTypeSearch] = useState('');
  const typeInputRef = useRef<HTMLInputElement>(null);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);

  // Convert activeTypes Set to array for API call
  const sheetTypesFilter = useMemo(() => {
    return activeTypes.size > 0 ? Array.from(activeTypes) : undefined;
  }, [activeTypes]);

  // Fetch page list from dedicated API (refetches when filters change)
  const { data: pageListData, isLoading: isLoadingPages, error: pagesError } = useGetQAResultPagesQuery(
    { 
      qa_result_id: selectedQAResultId!,
      sheet_types: sheetTypesFilter,
    },
    { 
      skip: !selectedQAResultId || !tenantId,
      // Refetch when filters change
      refetchOnMountOrArgChange: true,
    }
  );

  // Fetch page detail when a page is selected
  const { data: pageDetail, isLoading: isLoadingPageDetail } = useGetQAResultPageDetailQuery(
    {
      qa_result_id: selectedQAResultId!,
      page_number: selectedPageNumber!,
    },
    { skip: !selectedQAResultId || !selectedPageNumber || !tenantId }
  );

  // Group responses by category (category comes from backend)
  const groupedResponses = useMemo(() => {
    if (!pageDetail?.responses) return new Map<string, any[]>();
    
    const groups = new Map<string, any[]>();
    
    pageDetail.responses.forEach((response: any) => {
      // Category is already included in the response from backend
      const category = response.category || 'Unknown';
      
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(response);
    });
    
    return groups;
  }, [pageDetail]);

  // Use pages directly from API (already filtered server-side)
  const pages = pageListData?.pages || [];
  const totalPages = pageListData?.total_pages || 0;
  const filteredPages = pages; // Already filtered by API

  // Unique sheet types with counts from API response
  const sheetTypeCounts = useMemo(() => {
    if (!pageListData?.sheet_type_counts) return new Map<string, number>();
    return new Map(
      Object.entries(pageListData.sheet_type_counts).sort(([a], [b]) => a.localeCompare(b))
    );
  }, [pageListData]);

  // Types matching the search input (matching qa_viewer)
  const matchingTypes = useMemo(() => {
    if (!typeSearch) return [];
    const q = typeSearch.toLowerCase();
    return [...sheetTypeCounts.keys()].filter(
      (st) => st.toLowerCase().includes(q) && !activeTypes.has(st)
    );
  }, [typeSearch, sheetTypeCounts, activeTypes]);

  // Toggle type filter (matching qa_viewer)
  // This will trigger API refetch with new filters
  const toggleType = useCallback((st: string) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(st)) next.delete(st);
      else next.add(st);
      return next;
    });
    setTypeSearch('');
  }, []);

  // Use pageDetail from API instead of building from pages
  const selectedPage = pageDetail || null;

  // Auto-select first page if none selected - MUST be after filteredPages is defined
  React.useEffect(() => {
    if (selectedPageNumber === null && filteredPages.length > 0) {
      setSelectedPageNumber(filteredPages[0].page_number);
    }
  }, [filteredPages, selectedPageNumber]);

  if (!tenantId || !siteId) {
    return (
      <div className="w-full h-full bg-[var(--color-zone-recessed)] flex items-center justify-center">
        <div className="text-center eg-text-primary">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 eg-text-secondary" />
          <p className="text-lg">No Site Selected</p>
          <p className="text-sm mt-2 eg-text-secondary">Please select a site from the sidebar to view page list</p>
        </div>
      </div>
    );
  }

  // Show empty state if no QA results available
  if (!selectedQAResultId) {
    return (
      <div className="w-full h-full bg-[var(--color-zone-recessed)] flex items-center justify-center">
        <div className="text-center eg-text-primary">
          <FileText className="w-16 h-16 mx-auto mb-4 eg-text-secondary" />
          <p className="text-lg">No QA Result Available</p>
          <p className="text-sm mt-2 eg-text-secondary">
            {qaResultsData?.items && qaResultsData.items.length === 0
              ? 'No QA results found for this site. Run QA analysis first.'
              : 'Loading QA results...'}
          </p>
        </div>
      </div>
    );
  }

  if (isLoadingResults || isLoadingPages) {
    return (
      <div className="w-full h-full bg-[var(--color-zone-recessed)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-engramiq-green" />
        <span className="ml-3 eg-text-primary">Loading page list...</span>
      </div>
    );
  }

  if (pagesError) {
    return (
      <div className="w-full h-full bg-[var(--color-zone-recessed)] flex items-center justify-center">
        <div className="text-center eg-text-primary">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <p className="text-lg">Error Loading Page List</p>
          <p className="text-sm mt-2 eg-text-secondary">
            {pagesError && 'data' in pagesError ? JSON.stringify(pagesError.data) : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }


  return (
    <div className="w-full h-full flex" style={{ background: 'var(--color-zone-recessed)' }}>
      {/* Left: Page Sidebar */}
      <div className="w-60 flex-shrink-0 flex flex-col" style={{ background: 'var(--color-surface-elevated)', boxShadow: 'var(--shadow-sm)' }}>
        {/* Sidebar Header */}
        <div className="p-3 border-b border-[var(--color-border)] space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium eg-text-primary">Pages</span>
            <span className="text-xs eg-text-secondary">
              {filteredPages.length}/{totalPages}
            </span>
          </div>

          {/* Active filter chips */}
          {activeTypes.size > 0 && (
            <div className="flex flex-wrap gap-1">
              {[...activeTypes].map((st) => (
                <button
                  key={st}
                  onClick={() => toggleType(st)}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-engramiq-green text-engramiq-dark-blue rounded text-[10px] font-medium hover:bg-engramiq-bright-green transition-colors"
                >
                  {st}
                  <X size={10} />
                </button>
              ))}
              <button
                onClick={() => setActiveTypes(new Set())}
                className="px-1.5 py-0.5 eg-text-secondary hover:eg-text-primary rounded text-[10px]"
              >
                Clear
              </button>
            </div>
          )}

          {/* Type search input */}
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 eg-text-secondary" />
            <input
              ref={typeInputRef}
              type="text"
              placeholder="Filter by type..."
              value={typeSearch}
              onChange={(e) => setTypeSearch(e.target.value)}
              className="w-full pl-6 pr-2 py-1.5 border border-[var(--color-border)] rounded-md text-xs bg-[var(--color-surface)] eg-text-primary focus:bg-[var(--color-surface-hover)] focus:ring-1 focus:ring-engramiq-green focus:border-engramiq-green placeholder:text-[var(--color-text-secondary)]"
            />
            {/* Autocomplete dropdown */}
            {matchingTypes.length > 0 && (
              <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-[var(--color-zone-recessed)] border border-[var(--color-border)] rounded-md shadow-lg max-h-32 overflow-auto">
                {matchingTypes.map((st) => (
                  <button
                    key={st}
                    onClick={() => { toggleType(st); typeInputRef.current?.focus(); }}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-[var(--color-surface-hover)] eg-text-primary flex justify-between items-center"
                  >
                    <span>{st}</span>
                    <span className="eg-text-secondary">{sheetTypeCounts.get(st)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Page List */}
        <div className="flex-1 overflow-auto">
          {filteredPages.map((page) => (
            <PageSidebarItem
              key={page.page_number}
              page={page}
              isActive={page.page_number === selectedPageNumber}
              activeTypes={activeTypes}
              onToggleType={toggleType}
              onClick={() => {
                setSelectedPageNumber(page.page_number);
                window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
                  detail: { page: page.page_number },
                }));
              }}
            />
          ))}
          {filteredPages.length === 0 && (
            <div className="p-4 text-center text-xs eg-text-secondary">No pages match filters</div>
          )}
        </div>
      </div>

      {/* Right Side - Title Block & Details */}
      <div className="flex-1 flex flex-col min-w-0" style={{ background: 'var(--color-zone-recessed)' }}>
        {selectedPage ? (
          <div className="flex-1 overflow-auto p-4">
            {/* Title Block */}
            {isLoadingPageDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-engramiq-green" />
                <span className="ml-2 eg-text-primary">Loading page details...</span>
              </div>
            ) : selectedPage ? (
              <>
                {/* Title Block */}
                {selectedPage.titleblock && Object.keys(selectedPage.titleblock).length > 0 && (
                  <div className="eg-card-elevated p-4 mb-4">
                    <h3 className="font-semibold eg-text-primary mb-2">Title Block</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(selectedPage.titleblock)
                        .filter(([_, value]) => value)
                        .map(([key, value], index) => (
                          <div key={`${selectedPage.page_number}-${key}-${index}`}>
                            <span className="eg-text-secondary">{key.replace(/_/g, ' ')}: </span>
                            <span className="eg-text-primary">{String(value)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Equipment Labels */}
                {selectedPage.equipment_labels && Object.keys(selectedPage.equipment_labels).length > 0 && (
                  <div className="eg-card-elevated p-4 mb-4">
                    <h3 className="font-semibold eg-text-primary mb-2">Equipment Labels</h3>
                    <div className="space-y-2">
                      {Object.entries(selectedPage.equipment_labels).map(([type, labels]) => {
                        const labelArray = Array.isArray(labels) ? labels : [];
                        return (
                          <div key={type}>
                            <span className="text-xs eg-text-secondary uppercase">{type}</span>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {labelArray.map((label, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-0.5 bg-[var(--color-equipment-bg)] text-[var(--color-equipment-text)] rounded text-xs border border-[var(--color-equipment-border)]"
                                >
                                  {String(label)}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Responses grouped by category */}
                {groupedResponses.size > 0 && (
                  <div className="mb-4">
                    {Array.from(groupedResponses.entries()).map(([category, responses]) => (
                      <div key={category} className="mb-4">
                        <h3 className="font-semibold eg-text-primary mb-2 sticky top-0 py-1 z-10">
                          {category} ({responses.length})
                        </h3>
                        <div className="space-y-2">
                          {responses.map((response: any, i: number) => (
                            <QuestionCard
                              key={response.id || `response-${i}`}
                              response={response}
                              isSelected={selectedResponseId === response.id}
                              onClick={() => setSelectedResponseId(selectedResponseId === response.id ? null : response.id)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Page Reference Validations */}
                {selectedPage.page_reference_validations && selectedPage.page_reference_validations.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-3 sticky top-0 py-1 z-10">
                      <Link2 size={16} className="text-amber-400" />
                      <h3 className="font-semibold text-amber-400">
                        Page References ({selectedPage.page_reference_validations.length})
                      </h3>
                      {selectedPage.page_reference_validations.some((r: any) => !r.is_valid) && (
                        <span className="px-2 py-0.5 bg-red-400/20 text-red-400 rounded text-xs font-medium border border-red-400/30">
                          {selectedPage.page_reference_validations.filter((r: any) => !r.is_valid).length} invalid
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {selectedPage.page_reference_validations.map((result: any, i: number) => (
                        <PageRefCard key={result.id || i} result={result} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Nomenclature Deviations */}
                {selectedPage.nomenclature_deviations && selectedPage.nomenclature_deviations.length > 0 && (
                  <div className="eg-card-elevated p-4 mb-4">
                    <h3 className="font-semibold eg-text-primary mb-2">Nomenclature Deviations ({selectedPage.nomenclature_deviations.length})</h3>
                    <div className="space-y-2">
                      {selectedPage.nomenclature_deviations.map((dev: any, i: number) => (
                        <div key={i} className="text-sm eg-text-primary border-l-2 border-yellow-400/30 pl-2">
                          {dev.issue || dev.label || JSON.stringify(dev)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No details message */}
                {!selectedPage.titleblock && 
                 (!selectedPage.equipment_labels || Object.keys(selectedPage.equipment_labels).length === 0) &&
                 (!selectedPage.responses || selectedPage.responses.length === 0) && (
                  <div className="eg-card-elevated p-4">
                    <p className="text-sm eg-text-secondary">
                      Page {selectedPage.page_number} • {selectedPage.sheet_id || 'No sheet ID'}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="eg-card-elevated p-4">
                <p className="text-sm eg-text-secondary">Select a page to view details</p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-16 h-16 eg-text-secondary mx-auto mb-4" />
              <p className="eg-text-primary mb-2">Select a page to view details</p>
              <p className="text-sm eg-text-secondary">
                Click on a page from the list to see its title block and equipment information
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface PageSidebarItemProps {
  page: PageSummaryItem;
  isActive: boolean;
  activeTypes: Set<string>;
  onToggleType: (st: string) => void;
  onClick: () => void;
}

// PageSidebarItem - Dark theme
function PageSidebarItem({
  page,
  isActive,
  activeTypes,
  onToggleType,
  onClick,
}: PageSidebarItemProps) {
  const summary = page.summary;
  const total = summary.total;
  const passW = total > 0 ? (summary.pass / total) * 100 : 0;
  const failW = total > 0 ? (summary.fail / total) * 100 : 0;
  const caW = total > 0 ? (summary.ca / total) * 100 : 0;
  const hasFail = summary.fail > 0;

  return (
    <div
      className={`relative border-b border-[var(--color-border)] transition-colors cursor-pointer ${
        isActive
          ? 'bg-[var(--color-surface-hover)] shadow-sm border-l-[3px] border-l-engramiq-green'
          : hasFail
          ? 'hover:bg-[var(--color-surface-hover)] bg-[var(--color-surface)]'
          : 'hover:bg-[var(--color-surface-hover)] bg-[var(--color-surface)]'
      }`}
    >
      <div onClick={onClick} className="px-3 py-2">
        {/* Top row: page id + counts */}
        <div className="flex items-center justify-between gap-2">
          <span
            className={`text-xs font-semibold truncate ${
              isActive ? 'text-engramiq-green' : 'eg-text-primary'
            }`}
          >
            <span className="eg-text-muted font-normal">{page.page_number}.</span> {page.sheet_id || `P${page.page_number}`}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {summary.pass > 0 && (
              <span className="text-[10px] font-medium text-green-400 bg-green-400/20 px-1 rounded border border-green-400/30">
                {summary.pass}
              </span>
            )}
            {summary.fail > 0 && (
              <span className="text-[10px] font-medium text-red-400 bg-red-400/20 px-1 rounded border border-red-400/30">
                {summary.fail}
              </span>
            )}
            {summary.ca > 0 && (
              <span className="text-[10px] font-medium eg-text-secondary bg-gray-400/20 px-1 rounded border border-gray-400/30">
                {summary.ca}
              </span>
            )}
          </div>
        </div>

        {/* Title */}
        {page.sheet_title && (
          <p className="text-[11px] eg-text-secondary truncate mt-0.5 leading-tight">
            {page.sheet_title}
          </p>
        )}

        {/* Progress bar */}
        <div className="h-1 bg-[var(--color-surface)] rounded-full overflow-hidden flex mt-1.5">
          <div className="bg-green-400 h-full" style={{ width: `${passW}%` }} />
          <div className="bg-red-400 h-full" style={{ width: `${failW}%` }} />
          <div className="bg-gray-400 h-full" style={{ width: `${caW}%` }} />
        </div>
      </div>

      {/* Type chips — clickable to filter */}
      {page.sheet_types.length > 0 && (
        <div className="flex flex-wrap gap-0.5 px-3 pb-2 -mt-0.5">
          {page.sheet_types.map((st) => {
            const isFiltered = activeTypes.has(st);
            return (
              <button
                key={st}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleType(st);
                }}
                className={`px-1 py-0 rounded text-[10px] transition-colors ${
                  isFiltered
                    ? 'bg-engramiq-green text-engramiq-dark-blue'
                    : 'bg-engramiq-green/20 text-engramiq-green hover:bg-engramiq-green/30 border border-[var(--color-border)]'
                }`}
              >
                {st}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// QuestionCard component - matching qa_viewer style
interface QuestionCardProps {
  response: any;
  isSelected: boolean;
  onClick: () => void;
}

function QuestionCard({ response, isSelected, onClick }: QuestionCardProps) {
  // Question data comes from backend in response.question
  const question = response.question;
  const statusConfig = {
    pass: {
      icon: <CheckCircle size={16} />,
      bg: 'bg-green-400/10 border-green-400/30',
      iconColor: 'text-green-400',
      badge: 'bg-green-400/20 text-green-400 border border-green-400/30',
    },
    fail: {
      icon: <XCircle size={16} />,
      bg: 'bg-red-400/10 border-red-400/30',
      iconColor: 'text-red-400',
      badge: 'bg-red-400/20 text-red-400 border border-red-400/30',
    },
    ca: {
      icon: <AlertCircle size={16} />,
      bg: 'bg-gray-400/10 border-gray-400/30',
      iconColor: 'text-gray-300',
      badge: 'bg-gray-400/20 eg-text-secondary border border-gray-400/30',
    },
  };

  const answerType = response.answer_type || 'ca';
  const config = statusConfig[answerType as keyof typeof statusConfig] || statusConfig.ca;

  return (
    <div
      className={`rounded-lg border p-3 cursor-pointer transition-all ${config.bg} ${
        isSelected ? 'ring-2 ring-engramiq-green shadow-md' : 'hover:shadow-sm'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Status Icon */}
        <div className={`mt-0.5 ${config.iconColor}`}>{config.icon}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Question Description */}
          <p className="text-sm eg-text-primary font-medium">
            {question?.check_description || response.description || `Question ID: ${response.question_id}`}
          </p>

          {/* Badges Row */}
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}>
              {answerType === 'ca' ? "Can't Assess" : answerType.toUpperCase()}
            </span>
            {response.confidence !== undefined && (
              <span className="text-xs eg-text-secondary">
                Confidence: {Math.round(response.confidence)}%
              </span>
            )}
            {question?.difficulty && (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium border ${
                  question.difficulty === 'high'
                    ? 'bg-orange-400/20 text-orange-400 border-orange-400/30'
                    : question.difficulty === 'med'
                    ? 'bg-yellow-400/20 text-yellow-400 border-yellow-400/30'
                    : 'bg-engramiq-bright-grey/20 eg-text-secondary border-engramiq-bright-grey/30'
                }`}
              >
                {question.difficulty} difficulty
              </span>
            )}
            {response.response_type === 'category_group' && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-blue-400 bg-blue-400/10 border border-blue-400/30" title="Cross-page question">
                <GitCompare size={11} />
                Cross-page
              </span>
            )}
            {response.bounding_box && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
                    detail: { page: response.page_number, boundingBox: response.bounding_box },
                  }));
                }}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-engramiq-green bg-engramiq-green/10 hover:bg-engramiq-green/20 border border-engramiq-green/30 transition-colors cursor-pointer"
                title="View on As-Built drawing"
              >
                <Crosshair size={11} />
                Has location
              </button>
            )}
          </div>

          {/* Expanded Content */}
          {isSelected && (
            <div className="mt-3 pt-3 border-t border-[var(--color-border)] space-y-2">
              {/* AI Description */}
              {response.description && (
                <div>
                  <p className="text-xs eg-text-secondary mb-1">AI Description:</p>
                  <p className="text-sm eg-text-primary">{response.description}</p>
                </div>
              )}

              {/* Answer */}
              <div>
                <p className="text-xs eg-text-secondary mb-1">Answer:</p>
                <p className="text-sm eg-text-primary">{response.answer ? 'Yes' : 'No'}</p>
              </div>

              {/* Question Details */}
              {question && (
                <div className="text-xs eg-text-secondary space-y-1">
                  {question.category && <p>Category: {question.category}</p>}
                  {question.qc_type && <p>QC Type: {question.qc_type}</p>}
                  {question.validation_trigger && <p>Scope: {question.validation_trigger.toLowerCase() === 'immediate' ? 'Per Page' : question.validation_trigger}</p>}
                  {question.cross_reference_sheets && question.cross_reference_sheets.length > 0 && (
                    <p>Cross-ref: {question.cross_reference_sheets.join(', ')}</p>
                  )}
                </div>
              )}

              {/* Response Type */}
              {response.response_type && (
                <div className="text-xs eg-text-secondary">
                  <p>Response Type: {response.response_type}</p>
                </div>
              )}

              {/* Pages Referenced */}
              {response.pages_referenced && response.pages_referenced.length > 0 && (
                <div className="text-xs eg-text-secondary">
                  <p>Pages Referenced: {response.pages_referenced.join(', ')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Expand Indicator */}
        <div className="eg-text-secondary">
          {isSelected ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>
    </div>
  );
}

// PageRefCard component - matching qa_viewer style
interface PageRefCardProps {
  result: any;
}

function PageRefCard({ result }: PageRefCardProps) {
  const { reference, is_valid, target_found_in_toc, target_page, validation_description, confidence } = result;

  return (
    <div
      className={`eg-card-elevated p-4 border-l-4 ${
        is_valid ? 'border-l-green-400' : 'border-l-red-400'
      }`}
    >
      <div className="flex items-start gap-3">
        {is_valid ? (
          <CheckCircle size={18} className="text-green-400 flex-shrink-0 mt-0.5" />
        ) : (
          <XCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium eg-text-primary text-sm">
              {reference?.reference_text || 'Unknown reference'}
            </span>
            <span className="text-xs eg-text-secondary">→</span>
            <span className="font-mono text-xs bg-[var(--color-surface)] px-1.5 py-0.5 rounded eg-text-primary border border-[var(--color-border)]">
              {reference?.target_sheet_id || 'Unknown'}
            </span>
            {reference?.reference_type && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-purple-400/20 text-purple-300 border border-purple-400/30">
                {reference.reference_type.replace(/_/g, ' ')}
              </span>
            )}
          </div>

          {validation_description && (
            <p className="text-sm eg-text-primary mt-1">{validation_description}</p>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-2">
            {target_found_in_toc ? (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <BookOpen size={11} /> In TOC
              </span>
            ) : (
              <span className="text-xs text-red-400 flex items-center gap-1">
                <BookOpen size={11} /> Not in TOC
              </span>
            )}
            {target_page !== null && target_page !== undefined && (
              <span className="text-xs text-engramiq-green flex items-center gap-1">
                Target: Page {target_page}
                <ExternalLink size={10} />
              </span>
            )}
            <span className="text-xs eg-text-secondary">{Math.round(confidence)}% confidence</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Original structured format (kept for reference)
const PageItem: React.FC<PageItemProps> = ({ page }) => {
  const hasFailures = page.fail > 0;
  const hasCA = page.ca > 0;

  return (
    <div
      className={`eg-card-elevated p-2 transition-colors ${
        hasFailures
          ? 'border-l-2 border-l-red-400/30 hover:bg-[var(--color-surface-hover)]'
          : hasCA
          ? 'border-l-2 border-l-yellow-400/30 hover:bg-[var(--color-surface-hover)]'
          : 'hover:bg-[var(--color-surface-hover)]'
      }`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {/* Page Identifier */}
        {page.sheetId && (
          <span className="font-semibold eg-text-primary">{page.sheetId}</span>
        )}
        
        {/* Page Number */}
        <span className="eg-text-primary font-medium">{page.pageNumber}</span>
        
        {/* Counts */}
        {(page.pass > 0 || page.fail > 0 || page.ca > 0) && (
          <>
            {page.pass > 0 && (
              <span className="text-xs text-green-400">{page.pass}</span>
            )}
            {page.fail > 0 && (
              <span className="text-xs text-red-400">{page.fail}</span>
            )}
            {page.ca > 0 && (
              <span className="text-xs text-yellow-400">{page.ca}</span>
            )}
          </>
        )}

        {/* Sheet Title */}
        {page.sheetTitle && (
          <span className="eg-text-secondary font-medium">{page.sheetTitle}</span>
        )}

        {/* Sheet Types */}
        {page.sheetTypes.length > 0 && (
          <>
            {page.sheetTypes.map((st) => (
              <span
                key={st}
                className="px-1.5 py-0.5 bg-blue-400/20 text-blue-400 rounded text-xs font-medium"
              >
                {st}
              </span>
            ))}
          </>
        )}

        {/* Sheet Lists (lowercase with underscores) */}
        {page.sheetTypes.length > 0 && (
          <span className="text-xs eg-text-secondary">
            {page.sheetTypes.map((st, idx) => (
              <span key={idx}>
                {st.toLowerCase().replace(/\s+/g, '_')}
                {idx < page.sheetTypes.length - 1 && ' '}
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
};

export default PageListTab;
